import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Calendar, Phone, ChevronDown, ChevronUp, ShoppingBag, ArrowLeft, Download, CreditCard, Banknote, Building2, MapPin, Truck, Home } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Meeting point name mapping
const MEETING_POINT_NAMES = {
  'plaza_ciudad_jardin': 'Plaza Ciudad Jardín',
  'parque_del_pueblo': 'Parque del Pueblo',
  'metro_pantitlan': 'Metro Pantitlán',
  'plaza_aragon': 'Plaza Aragón',
  'centro_nezahualcoyotl': 'Centro de Nezahualcóyotl'
};

const OrderHistory = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [downloadingTicket, setDownloadingTicket] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleDownloadTicket = async (orderId) => {
    try {
      setDownloadingTicket(orderId);
      const response = await axios.get(
        `${API}/orders/${orderId}/ticket-base64`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Create download link
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${response.data.pdf_base64}`;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Ticket descargado');
    } catch (error) {
      toast.error('Error al descargar el ticket');
    } finally {
      setDownloadingTicket(null);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      setOrders([]);
      toast.error('No se pudieron cargar los pedidos');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  const getStatusBadge = (status, paymentStatus) => {
    if (paymentStatus === 'paid') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Pagado
        </span>
      );
    }
    if (paymentStatus === 'pending_verification') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Esperando comprobante
        </span>
      );
    }
    if (status === 'awaiting_payment') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Esperando pago
        </span>
      );
    }
    if (status === 'pending' || status === 'pending_confirmation') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Pendiente
        </span>
      );
    }
    if (status === 'completed') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Completado
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        {status}
      </span>
    );
  };

  const getPaymentMethodBadge = (paymentMethod) => {
    if (paymentMethod === 'cash_on_delivery') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <Banknote className="w-3 h-3 mr-1" />
          Contra Entrega
        </span>
      );
    }
    if (paymentMethod === 'bank_transfer') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Building2 className="w-3 h-3 mr-1" />
          Transferencia
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
        <CreditCard className="w-3 h-3 mr-1" />
        Tarjeta
      </span>
    );
  };

  const toggleOrderExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#FAFAF9]">
        <div className="container-luxury py-16">
          <div className="max-w-4xl mx-auto">
            {/* Loading skeleton */}
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-[#F5F0EB] rounded w-1/3"></div>
              <div className="h-4 bg-[#F5F0EB] rounded w-1/4"></div>
              {[1, 2, 3].map((num) => (
                <div key={`skeleton-order-${num}`} className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="h-6 bg-[#F5F0EB] rounded w-1/2 mb-4"></div>
                  <div className="h-4 bg-[#F5F0EB] rounded w-1/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#FAFAF9]" data-testid="order-history-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E7E5E4]">
        <div className="container-luxury py-12 md:py-16">
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-sm text-[#57534E] hover:text-[#BA7B7C] transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Volver al inicio</span>
          </Link>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-[#FDF8F6] rounded-full flex items-center justify-center">
              <Package className="w-6 h-6 text-[#BA7B7C]" />
            </div>
            <div>
              <h1 
                className="text-3xl md:text-4xl font-light tracking-tight text-[#1C1917]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Mis Pedidos
              </h1>
              <p className="text-[#A8A29E] mt-1">
                {orders.length} {orders.length === 1 ? 'pedido' : 'pedidos'} en total
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container-luxury py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          {orders.length === 0 ? (
            <div className="text-center py-16" data-testid="no-orders">
              <div className="w-20 h-20 bg-[#FDF8F6] rounded-full flex items-center justify-center mx-auto mb-6">
                <ShoppingBag className="w-10 h-10 text-[#BA7B7C]" />
              </div>
              <h2 
                className="text-2xl font-light text-[#1C1917] mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                No tienes pedidos aún
              </h2>
              <p className="text-[#A8A29E] mb-8">
                Explora nuestra colección y realiza tu primera compra
              </p>
              <Link 
                to="/shop" 
                className="btn-primary inline-flex items-center space-x-2"
              >
                <span>Explorar tienda</span>
              </Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="orders-list">
              {orders.map((order) => (
                <div 
                  key={order.id} 
                  className="bg-white rounded-2xl shadow-sm overflow-hidden"
                  data-testid={`order-${order.id}`}
                >
                  {/* Order Header - Always visible */}
                  <button
                    onClick={() => toggleOrderExpand(order.id)}
                    className="w-full px-6 py-5 flex items-center justify-between hover:bg-[#FAFAF9] transition-colors"
                    data-testid={`order-toggle-${order.id}`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="text-left">
                        <p className="text-sm font-medium text-[#1C1917]">
                          Pedido #{order.order_number || order.id.slice(0, 8).toUpperCase()}
                        </p>
                        <div className="flex items-center space-x-2 mt-1 text-sm text-[#A8A29E]">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(order.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 md:space-x-4 flex-wrap justify-end gap-1">
                      {getPaymentMethodBadge(order.payment_method)}
                      {getStatusBadge(order.status, order.payment_status)}
                      <span className="font-medium text-[#1C1917]">
                        {formatPrice(order.total)}
                      </span>
                      {expandedOrder === order.id ? (
                        <ChevronUp className="w-5 h-5 text-[#A8A29E]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#A8A29E]" />
                      )}
                    </div>
                  </button>

                  {/* Order Details - Expandable */}
                  {expandedOrder === order.id && (
                    <div className="px-6 pb-6 border-t border-[#E7E5E4]" data-testid={`order-details-${order.id}`}>
                      {/* Payment Method & Download Ticket */}
                      <div className="flex items-center justify-between py-4 border-b border-[#E7E5E4]">
                        <div className="flex items-center space-x-4">
                          {order.phone_number && (
                            <div className="flex items-center space-x-2">
                              <Phone className="w-4 h-4 text-[#25D366]" />
                              <a 
                                href={`https://wa.me/${order.phone_number.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-[#25D366] hover:underline"
                              >
                                {order.phone_number}
                              </a>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadTicket(order.id);
                          }}
                          disabled={downloadingTicket === order.id}
                          className="flex items-center space-x-2 px-4 py-2 bg-[#1C1917] text-white text-sm rounded-lg hover:bg-[#44403C] transition-colors disabled:opacity-50"
                          data-testid={`download-ticket-${order.id}`}
                        >
                          {downloadingTicket === order.id ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          <span>Descargar Ticket</span>
                        </button>
                      </div>

                      {/* Products List */}
                      <div className="py-4">
                        <h4 className="text-sm font-medium text-[#1C1917] mb-4">
                          Productos ({order.items.length})
                        </h4>
                        <div className="space-y-4">
                          {order.items.map((item) => (
                            <div 
                              key={`${order.id}-${item.product_id}`} 
                              className="flex items-center space-x-4"
                              data-testid={`order-item-${item.product_id}`}
                            >
                              <div className="w-16 h-16 bg-[#F5F0EB] rounded-xl overflow-hidden flex-shrink-0">
                                <img 
                                  src={item.image_url} 
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/64?text=IMG';
                                  }}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#1C1917] truncate">
                                  {item.name}
                                </p>
                                <p className="text-sm text-[#A8A29E]">
                                  Cantidad: {item.quantity} × {formatPrice(item.price)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium text-[#1C1917]">
                                  {formatPrice(item.subtotal)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Order Summary */}
                      <div className="pt-4 border-t border-[#E7E5E4]">
                        {/* Delivery Information */}
                        {order.delivery_method && (
                          <div className="mb-4 pb-4 border-b border-[#E7E5E4]">
                            <h4 className="text-sm font-medium text-[#1C1917] mb-3 flex items-center">
                              <Truck className="w-4 h-4 mr-2 text-[#BA7B7C]" />
                              Información de Entrega
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex items-start">
                                <span className="text-[#A8A29E] w-32">Método:</span>
                                <span className="text-[#1C1917] flex items-center">
                                  {order.delivery_method === 'meeting_point' ? (
                                    <>
                                      <MapPin className="w-3 h-3 mr-1" />
                                      Punto de Encuentro
                                    </>
                                  ) : (
                                    <>
                                      <Home className="w-3 h-3 mr-1" />
                                      Envío a Domicilio
                                    </>
                                  )}
                                </span>
                              </div>
                              
                              {order.delivery_method === 'meeting_point' && order.meeting_point && (
                                <div className="flex items-start">
                                  <span className="text-[#A8A29E] w-32">Punto:</span>
                                  <span className="text-[#1C1917]">
                                    {MEETING_POINT_NAMES[order.meeting_point] || order.meeting_point}
                                  </span>
                                </div>
                              )}
                              
                              {order.delivery_method === 'shipping' && order.shipping_address && (
                                <>
                                  <div className="flex items-start">
                                    <span className="text-[#A8A29E] w-32">Dirección:</span>
                                    <span className="text-[#1C1917]">
                                      {order.shipping_address.street} {order.shipping_address.exterior_number}
                                      {order.shipping_address.interior_number && `, Int. ${order.shipping_address.interior_number}`}
                                      <br />
                                      {order.shipping_address.neighborhood}, {order.shipping_address.municipality}
                                      <br />
                                      {order.shipping_address.state}, C.P. {order.shipping_address.postal_code}
                                    </span>
                                  </div>
                                  {order.shipping_company && (
                                    <div className="flex items-start">
                                      <span className="text-[#A8A29E] w-32">Paquetería:</span>
                                      <span className="text-[#1C1917]">{order.shipping_company}</span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-[#1C1917]">Total del pedido</span>
                          <span className="text-lg font-medium text-[#BA7B7C]">
                            {formatPrice(order.total)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderHistory;
